import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { BarcodeType1, BarcodeType1ListResponse, BarcodeType1Create } from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10;

interface UseBarcodeType1Result {
  BarcodeType1: BarcodeType1[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number; // Added totalItems to the result interface
  fetchBarcodeType1: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createBarcodeType1: (data: BarcodeType1Create) => Promise<boolean>;
  updateBarcodeType1: (id: number, data: BarcodeType1Create) => Promise<boolean>;
  deleteBarcodeType1: (id: number) => Promise<boolean>;
}

export const useBarcodeType1 = (): UseBarcodeType1Result => {
  const [BarcodeType1, setBarcodeType1] = useState<BarcodeType1[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0); // New state for totalItems
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBarcodeType1 = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<BarcodeType1ListResponse>(
        `/barcode_1?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setBarcodeType1(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items); // Set totalItems from response
      // REMOVED: setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch Barcode Type 1s: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching Barcode Type 1s.');
      }
      setBarcodeType1([]);
      setTotalItems(0); // Reset totalItems on error
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchBarcodeType1(currentPage);
  }, [currentPage, fetchBarcodeType1]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createBarcodeType1 = async (data: BarcodeType1Create): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<BarcodeType1>('/barcode_1', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      //alert('Barcode Type 1 created successfully!');
      await fetchBarcodeType1(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create Barcode Type 1: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateBarcodeType1 = async (id: number, data: BarcodeType1Create): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<BarcodeType1>(`/barcode_1/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      //alert('Barcode Type 1 updated successfully!');
      await fetchBarcodeType1(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update Barcode Type 1: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteBarcodeType1 = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Barcode Type 1 ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/barcode_1/${id}`, { method: 'DELETE' });
      //alert(`Barcode Type 1 ${id} deleted successfully!`);
      const pageToFetch = BarcodeType1.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchBarcodeType1(pageToFetch); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete Barcode Type 1: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    BarcodeType1,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems, // Return totalItems
    fetchBarcodeType1,
    handlePageChange,
    createBarcodeType1,
    updateBarcodeType1,
    deleteBarcodeType1,
  };
};